import { API, graphqlOperation } from 'aws-amplify';

import * as mutations from '../../graphql/mutations';
import * as queries from '../../graphql/queries';

const paginate = async (query, accessor, variables = {}) => {
  const items = [];
  let nextToken = null;
  do {
    // eslint-disable-next-line no-await-in-loop
    const response = await API.graphql(graphqlOperation(query, { ...variables, limit: 100000, nextToken }));
    items.push(...(response?.data?.[accessor]?.items ?? []));
    nextToken = response?.data?.[accessor]?.nextToken;
  } while (nextToken);
  return items;
};

export const listAllDmsDrivers = async () => {
  try {
    return await paginate(queries.listDmsDrivers, 'listDmsDrivers');
  } catch (e) {
    console.log('listAllDmsDrivers error', e);
    throw new Error('Failed to load drivers. Please try again later.');
  }
};

export const getDmsDriver = async id => {
  try {
    const response = await API.graphql(graphqlOperation(queries.getDmsDriver, { id }));
    return response?.data?.getDmsDriver;
  } catch (e) {
    console.log('getDmsDriver error', e);
    throw new Error('Failed to load driver. Please try again later.');
  }
};

export const listActivitiesByDriver = async driverId => {
  try {
    return await paginate(
      queries.listDmsContractActivitiesByDriverIdByContractStartDate,
      'listDmsContractActivitiesByDriverIdByContractStartDate',
      { driverId },
    );
  } catch (e) {
    console.log('listActivitiesByDriver error', e);
    throw new Error('Failed to load contract activities. Please try again later.');
  }
};

export const listAllContractActivities = async () => {
  try {
    return await paginate(queries.listDmsContractActivities, 'listDmsContractActivities');
  } catch (e) {
    console.log('listAllContractActivities error', e);
    throw new Error('Failed to load contract activities. Please try again later.');
  }
};

export const getDmsContractActivity = async id => {
  try {
    const response = await API.graphql(graphqlOperation(queries.getDmsContractActivity, { id }));
    return response?.data?.getDmsContractActivity;
  } catch (e) {
    console.log('getDmsContractActivity error', e);
    throw new Error('Failed to load the contract activity. Please try again later.');
  }
};

// Recompute the denormalized rollups the list pages read off DmsDriver
// (badges, current client, latest end date) from the driver's activities.
const refreshDriverRollups = async driverId => {
  const activities = await listActivitiesByDriver(driverId);
  if (!activities.length) return;
  const latest = activities.reduce((max, a) => ((a.contractEndDate || '') > (max.contractEndDate || '') ? a : max));
  const input = {
    id: String(driverId),
    serviceAction: activities.some(a => !a.clearServiceActionFlag),
    benefitsAction: activities.some(a => !a.clearBenefitActionFlag),
    currentClientId: latest.clientId,
    currentClientName: latest.clientName,
    latestActivityEndDate: latest.contractEndDate,
  };
  await API.graphql(graphqlOperation(mutations.updateDmsDriver, { input }));
};

export const saveContractActivity = async input => {
  let saved;
  try {
    const response = await API.graphql(graphqlOperation(mutations.updateDmsContractActivity, { input }));
    saved = response?.data?.updateDmsContractActivity;
  } catch (e) {
    console.log('saveContractActivity error', e);
    throw new Error('Failed to save. Please try again later.');
  }
  // The activity is saved at this point — a rollup refresh failure should not
  // surface as a failed save; the integration job trues the rollups up anyway.
  if (saved?.driverId) {
    try {
      await refreshDriverRollups(saved.driverId);
    } catch (e) {
      console.log('refreshDriverRollups error', e);
    }
  }
  return saved;
};

// Active options for the Benefit section dropdowns (UC 5.3 / 6.2)
const benefitListAccessors = {
  classCodes: 'listBenefitClassCodes',
  divisionCodes: 'listBenefitDivisionCodes',
  planTypes: 'listBenefitPlanTypes',
  typesOfCoverage: 'listBenefitTypeOfCoverages',
};

export const getBenefitDropdownOptions = async () => {
  const entries = await Promise.all(
    Object.entries(benefitListAccessors).map(async ([key, accessor]) => {
      try {
        const items = await paginate(queries[accessor], accessor);
        const options = items
          .filter(item => item.status?.toLowerCase() === 'active')
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(({ name }) => ({ label: name, value: name }));
        return [key, options];
      } catch (e) {
        console.log(`getBenefitDropdownOptions ${key} error`, e);
        return [key, []];
      }
    }),
  );
  return Object.fromEntries(entries);
};

// Edge lookup for the Medical Contract Activity ID (UC 5.3). Served by the
// dqf-apiv2 endpoint GET contract-activities/{id} (added alongside CP-835).
export const getEdgeContractActivity = async contractActivityId => {
  const response = await API.get('centerlineportal', `contract-activities/${contractActivityId}`);
  if (response.response_code === 200) {
    return response.response_body;
  }
  throw new Error('Contract activity not found in Edge.');
};
